import React from "react";
import "./terms.css";

export default function Terms() {
  return (
    <main className="terms">
      <h1 className="mb-2">Terms of Service</h1>
      <p className="mt-0 text-sm">Last updated: {new Date().toLocaleDateString()}</p>

      <div className="rounded-2xl p-4 bg-yellow-50 border border-yellow-200 text-yellow-900">
        <strong>Heads up:</strong> This page is a template. It aims to protect <em>GK Technologies</em> ("GK",
        "we", "us", or "our") with strong limitations of liability, warranty disclaimers, and an
        at-will cancellation policy. Please have counsel review.
      </div>

      <nav className="not-prose mt-6">
        <ol className="grid gap-2 list-decimal pl-5 text-sm">
          <li><a className="underline" href="#acceptance">Acceptance of Terms</a></li>
          <li><a className="underline" href="#account">Accounts & Eligibility</a></li>
          <li><a className="underline" href="#license">License & Use</a></li>
          <li><a className="underline" href="#data">Data; Security & Privacy Disclaimer</a></li>
          <li><a className="underline" href="#ai">AI Outputs & No Professional Advice</a></li>
          <li><a className="underline" href="#fees">Fees, Subscription & Cancellation</a></li>
          <li><a className="underline" href="#refunds">Refunds</a></li>
          <li><a className="underline" href="#thirdparty">Third‑Party Services</a></li>
          <li><a className="underline" href="#prohibited">Prohibited Uses</a></li>
          <li><a className="underline" href="#availability">Availability; Changes; Beta Features</a></li>
          <li><a className="underline" href="#warranties">No Warranties</a></li>
          <li><a className="underline" href="#liability">Limitation of Liability</a></li>
          <li><a className="underline" href="#indemnification">Indemnification</a></li>
          <li><a className="underline" href="#ip">Intellectual Property</a></li>
          <li><a className="underline" href="#privacy">Privacy</a></li>
          <li><a className="underline" href="#govlaw">Governing Law; Dispute Resolution</a></li>
          <li><a className="underline" href="#termination">Termination</a></li>
          <li><a className="underline" href="#changes">Changes to These Terms</a></li>
          <li><a className="underline" href="#contact">Contact</a></li>
        </ol>
      </nav>

      <section id="acceptance">
        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service (the "Terms") govern your access to and use of the Taren service, websites,
          applications, and related services provided by GK (collectively, the "Service"). By creating an account,
          accessing, or using the Service, you agree to be bound by these Terms.
        </p>
      </section>

      <section id="account">
        <h2>2. Accounts & Eligibility</h2>
        <p>
          You must be at least the age of majority in your jurisdiction and have authority to bind the
          organization you represent. You are responsible for the accuracy of your account information and for
          maintaining the confidentiality of your login credentials. You are responsible for all activity that
          occurs under your account.
        </p>
      </section>

      <section id="license">
        <h2>3. License & Use</h2>
        <p>
          Subject to these Terms and timely payment of applicable fees, GK grants you a limited, revocable,
          non‑exclusive, non‑transferable license to access and use the Service for your internal business
          purposes. Except as expressly permitted, you may not copy, modify, distribute, sell, lease, reverse
          engineer, or create derivative works of the Service.
        </p>
      </section>

      <section id="data">
        <h2>4. Data; Security & Privacy Disclaimer</h2>
        <p>
          You may upload or otherwise provide data, files, prompts, or other content ("Customer Data") to the
          Service. You represent that you have all necessary rights to Customer Data. You are solely responsible
          for the legality, quality, and accuracy of Customer Data and how you acquire it.
        </p>
        <p>
          <strong>Security & Privacy Disclaimer.</strong> While GK endeavors to employ commercially reasonable
          safeguards, the Service is provided "as is" and <strong>GK does not guarantee or warrant the security,
          confidentiality, availability, or integrity of Customer Data</strong>. You acknowledge that transmitting
          data over the internet and storing data involves inherent risks. <strong>To the maximum extent permitted by
          law, GK disclaims any responsibility or liability for unauthorized access to, use of, alteration of, or
          loss of Customer Data</strong>. You are responsible for maintaining appropriate backups and for
          implementing security controls in your own environment, networks, and devices.
        </p>
        <p>
          If you upload regulated, sensitive, or personal data, you are solely responsible for ensuring
          compliance with applicable laws (e.g., privacy, consumer, financial, health). Unless agreed in a signed
          written addendum, GK does not process data under sector‑specific compliance frameworks (e.g., HIPAA,
          GLBA, or similar) and the Service is not intended for such data types.
        </p>
      </section>

      <section id="ai">
        <h2>5. AI Outputs & No Professional Advice</h2>
        <p>
          The Service may generate text, reports, charts, or other outputs based on inputs and models ("Outputs").
          Outputs may be inaccurate, incomplete, or require human review. <strong>Outputs are provided for
          informational purposes only</strong> and do not constitute financial, legal, accounting, medical, or other
          professional advice. You are solely responsible for evaluating and using Outputs and for any decisions
          made in reliance on them.
        </p>
      </section>

      <section id="fees">
        <h2>6. Fees, Subscription & Cancellation</h2>
        <p>
          Access to certain features may require payment of fees on a one‑time or subscription basis. By initiating
          a purchase, you authorize GK (or its processor) to charge your payment method for the applicable amounts
          and taxes. Subscriptions automatically renew at the then‑current rate until cancelled.
        </p>
        <p>
          <strong>Cancel Anytime.</strong> <em>You or GK may cancel a subscription at any time</em> for convenience.
          If you cancel, you will retain access through the end of the current billing period unless otherwise
          stated. GK may cancel or suspend access immediately for violation of these Terms, suspected fraud, or
          to comply with law.
        </p>
      </section>

      <section id="refunds">
        <h2>7. Refunds</h2>
        <p>
          Except where required by law or expressly stated otherwise in writing, all fees are non‑refundable.
          GK may, in its sole discretion, choose to issue goodwill credits.
        </p>
      </section>

      <section id="thirdparty">
        <h2>8. Third‑Party Services</h2>
        <p>
          The Service may interoperate with third‑party products or services (e.g., payment processors, data
          connectors, cloud platforms). GK does not control and is not responsible for third‑party services and
          disclaims all liability related to them. Your use of third‑party services is governed by their terms and
          policies.
        </p>
      </section>

      <section id="prohibited">
        <h2>9. Prohibited Uses</h2>
        <ul>
          <li>Illegal, harmful, infringing, or deceptive activities;</li>
          <li>Uploading or processing data you lack rights to use;</li>
          <li>Attempting to probe, scan, or test the vulnerability of the Service;</li>
          <li>Reverse engineering or circumventing security or access controls;</li>
          <li>Training models that compete with the Service, except as allowed by law;</li>
          <li>Using Outputs to create misleading claims presented as facts without adequate verification.</li>
        </ul>
      </section>

      <section id="availability">
        <h2>10. Availability; Changes; Beta Features</h2>
        <p>
          GK may modify, suspend, or discontinue the Service (or any feature) at any time. Beta or experimental
          features may be offered on an "as available" basis and may be withdrawn without notice. Performance and
          uptime are not guaranteed.
        </p>
      </section>

      <section id="warranties">
        <h2>11. No Warranties</h2>
        <p>
          <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND</strong>,
          whether express, implied, or statutory, including without limitation warranties of merchantability,
          fitness for a particular purpose, title, non‑infringement, accuracy, or quiet enjoyment. You bear all
          risk as to the quality and performance of the Service and Outputs.
        </p>
      </section>

      <section id="liability">
        <h2>12. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, <strong>GK WILL NOT BE LIABLE</strong> for any indirect,
          incidental, special, consequential, exemplary, or punitive damages; loss of profits, revenues, goodwill,
          data, or business interruption; or cost of substitute goods or services, arising out of or related to
          the Service or these Terms, even if advised of the possibility of such damages.
        </p>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, GK’s total cumulative liability for all claims arising out of or
          related to the Service or these Terms <strong>will not exceed the greater of</strong>: (a) the amounts you
          actually paid to GK for the Service giving rise to the claim in the <strong>12 months</strong> preceding the
          claim; or (b) <strong>US $100</strong>.
        </p>
      </section>

      <section id="indemnification">
        <h2>13. Indemnification</h2>
        <p>
          You will defend, indemnify, and hold harmless GK and its affiliates, officers, directors, employees,
          and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable
          attorneys’ fees) arising out of or related to: (a) your Customer Data; (b) your use of the Service or
          Outputs; (c) your violation of these Terms; or (d) your violation of any law or third‑party right.
        </p>
      </section>

      <section id="ip">
        <h2>14. Intellectual Property</h2>
        <p>
          GK and its licensors retain all right, title, and interest in and to the Service, software, models,
          and all related intellectual property. Except for the limited license granted above, no rights are
          granted by implication, estoppel, or otherwise.
        </p>
      </section>

      <section id="privacy">
        <h2>15. Privacy</h2>
        <p>
          Your use of the Service is also subject to our Privacy Policy, which describes how we collect and use
          certain information. <strong>GK does not assume any duty to maintain the confidentiality of Customer
          Data and disclaims liability for privacy or security incidents</strong>, except to the limited extent
          required by applicable law and as expressly set forth in a separate signed agreement, if any.
        </p>
      </section>

      <section id="govlaw">
        <h2>16. Governing Law; Dispute Resolution</h2>
        <p>
          These Terms are governed by the laws of the State of Delaware, without regard to its conflict of laws
          rules. Any dispute will be resolved through <strong>binding arbitration</strong> administered by JAMS under
          its Streamlined Rules. The seat of arbitration is Wilmington, Delaware, and the language is English.
          <strong>Class actions and jury trials are waived</strong> to the fullest extent permitted by law. You and GK
          may seek injunctive or equitable relief in a court of competent jurisdiction to protect IP or
          confidential information.
        </p>
      </section>

      <section id="termination">
        <h2>17. Termination</h2>
        <p>
          Either party may terminate these Terms (and any subscription) at any time for convenience. Upon
          termination, your license and access to the Service cease. Sections that by their nature should survive
          (e.g., fees owed, confidentiality, IP, no warranties, limitation of liability, indemnification, dispute
          resolution) will survive.
        </p>
      </section>

      <section id="changes">
        <h2>18. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. If changes are material, we will provide notice by
          posting an updated version here or via the Service. Changes take effect upon posting unless otherwise
          stated. Your continued use of the Service after changes become effective constitutes acceptance.
        </p>
      </section>

      <section id="contact">
        <h2>19. Contact</h2>
        <p>
          GK Technologies, LLC<br />
          Email: sam@technologiesgk.com<br />
          Address: 8 The Green Suite B, Dover, DE 19901 USA
        </p>
      </section>

      <hr />
      <p className="text-xs opacity-70">
        If you are implementing Stripe Checkout, consider requiring explicit consent to these Terms during
        checkout and storing the Checkout Session ID with a Terms version hash for auditability.
      </p>
    </main>
  );
}
